import { create } from 'zustand';
import { MODES } from '@scio3/shared-types';

/** Subset of MODES used in schedule sidebar */
export type ScheduleSidebarMode = typeof MODES.DEFAULT | typeof MODES.SHARE_WITH_ME;

/** GAP-03: Search scope controls whether search queries current folder or all schedules */
export type SearchScope = 'all' | 'folder';

interface ScheduleSidebarState {
  searchText: string;
  mode: ScheduleSidebarMode;
  /** GAP-03: Whether to search all schedules or only within current folder */
  searchScope: SearchScope;
  /** When true, show only the current user's favorited schedules + folders (server-side). */
  favoritesOnly: boolean;
  collapsed: boolean;
  /** Signal to scroll sidebar to a specific schedule after breadcrumb navigation */
  scrollToItemId: string | null;
  /** Whether the active schedule is currently visible in the sidebar scroll area */
  isItemVisible: boolean;
  setSearchText: (text: string) => void;
  setMode: (mode: ScheduleSidebarMode) => void;
  setSearchScope: (scope: SearchScope) => void;
  setFavoritesOnly: (favoritesOnly: boolean) => void;
  toggleCollapsed: () => void;
  clearSearch: () => void;
  requestScrollToItem: (id: string) => void;
  clearScrollToItem: () => void;
  setItemVisible: (visible: boolean) => void;
}

export const useScheduleSidebarStore = create<ScheduleSidebarState>((set, get) => ({
  searchText: '',
  mode: MODES.DEFAULT,
  searchScope: 'folder',
  favoritesOnly: false,
  collapsed: false,
  scrollToItemId: null,
  isItemVisible: false,
  setSearchText: (searchText) => set({ searchText }),
  setMode: (mode) => set({ mode, searchText: '', favoritesOnly: false }),
  setSearchScope: (searchScope) => set({ searchScope }),
  setFavoritesOnly: (favoritesOnly) => set({ favoritesOnly }),
  toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),
  clearSearch: () =>
    set((s) => ({
      searchText: '',
      searchScope: 'folder',
      mode: s.mode === MODES.SHARE_WITH_ME ? MODES.SHARE_WITH_ME : MODES.DEFAULT,
    })),
  requestScrollToItem: (id) => set({ scrollToItemId: id }),
  clearScrollToItem: () => set({ scrollToItemId: null }),
  setItemVisible: (visible) => {
    if (get().isItemVisible !== visible) set({ isItemVisible: visible });
  },
}));
